<script lang="ts">
import { 
        releaseStore,
        computedStep,
        releaseRuntimeState,
        releaseConfigState
    } from '../stores/release';
    import { ReleaseClient } from '../api/client';
    import { useReleaseWorkflow, type WorkflowCallbacks } from '../services/useReleaseWorkflow';
    import { get } from 'svelte/store';
	
	// Import step components
	import ReleaseForm from './steps/ReleaseForm.svelte';
import XMLDisplay from './steps/XMLDisplay.svelte';
import AssetGenerationPanel from './AssetGenerationPanel.svelte';
import DeliveryDisplay from './steps/DeliveryDisplay.svelte';
import ErrorDisplay from './steps/ErrorDisplay.svelte';
import { provideReleaseContext } from '../context/release';

    const client = new ReleaseClient();
    
    // Phase 2: New workflow controller with callbacks
    // Clean separation: workflow -> callbacks -> stores -> UI
    const callbacks: WorkflowCallbacks = {
        onGenerationStart: () => {
            console.log('🚀 Starting XML generation...');
            releaseStore.setGenerationRunning();
        },
        onGenerationComplete: (data) => {
            console.log('✅ Generation complete:', data);
            releaseStore.setReleaseData(data);
            // Update config with generated releaseId
            releaseConfigState.update(msg => ({ ...msg, releaseId: data.releaseId }));
        },
        onValidationComplete: (data) => {
            console.log('✅ Validation complete:', data);
            releaseStore.setValidation(data);
        },
        onDeliveryStart: () => {
            console.log('📦 Starting delivery...');
            releaseStore.setDeliveryInitiated();
        },
        onDeliveryComplete: (filePath) => {
            console.log('✅ Delivery complete:', filePath);
            releaseStore.setDeliveryPath(filePath);
        },
        onError: (error) => {
            console.error('💥 Workflow error:', error.message);
            releaseStore.setError(error.message);
        }
    };
    
    const workflow = useReleaseWorkflow({ client, callbacks });

    // Phase 3: Provide cleaner context with just core stores
    provideReleaseContext({
	releaseConfigState,
	releaseRuntimeState,
	releaseStore
    });

	let isApple = $derived(($releaseConfigState.outputFormat ?? 'ddex-ern') === 'apple-itunes');

	// Phase 6: Svelte 5 runes! 🚀
	// Derived values - automatically recompute when dependencies change
	let steps = $derived($releaseConfigState.generateAiAssets 
		? ['input', 'generating-xml', 'preview', 'delivering', 'complete']
		: ['input', 'generating-xml', 'preview', 'delivering', 'complete']);
	
	let totalSteps = $derived(steps.filter(step => step !== 'complete').length);
	
    let currentStepNumber = $derived.by(() => {
        const step = $computedStep;
        if (step === 'complete') {
            return totalSteps;
        }
        return steps.indexOf(step) + 1;
    });

	// Progress calculation for step indicator animation
    let stepProgress = $derived.by(() => {
        if ($computedStep !== 'preview') return 0;
        if ($releaseRuntimeState.assetGeneration.results) return 100;
        if ($releaseRuntimeState.assetGeneration.error) return 0;
        if (!$releaseRuntimeState.assetGeneration.isGenerating && !$releaseRuntimeState.assetGeneration.progress) return 0;

		const message = $releaseRuntimeState.assetGeneration.progress?.toLowerCase() || '';
		
		// Check for completion messages first
		if (message.includes('complet') || message.includes('finish') || message.includes('all assets generated')) return 100;
		
		// More accurate progress mapping based on actual workflow
		if (message.includes('connect') || message.includes('starting')) return 5;
		if (message.includes('stream') || message.includes('status')) return 15;
		if (message.includes('analyz') || message.includes('process') || message.includes('prepar')) return 30;
		if (message.includes('generat') && message.includes('image')) return 50;
		if (message.includes('generat') && message.includes('audio')) return 70;
		if (message.includes('generat') && message.includes('video')) return 85;
		if (message.includes('image') && message.includes('complet')) return 60;
		if (message.includes('audio') && message.includes('complet')) return 80;
		if (message.includes('video') && message.includes('complet')) return 95;
		if (message.includes('generat')) return 60; // Generic generation message
		
		// Default based on generation state
		return $releaseRuntimeState.assetGeneration.isGenerating ? 40 : 0;
	});

    // previewStep is already set by handlePreviewRelease — no additional effect needed.

    async function handleFormSubmit() {
        try {
            // Phase 2: Pass explicit parameters instead of reading from stores
            const request = get(releaseConfigState);
            console.log('🚀 Form submitted with data:', request);
            
            await workflow.runFullWorkflow(request);
            console.log('🎉 XML generation and validation complete!');
        } catch (e) {
            // Error already handled by onError callback
            console.error('💥 Error in handleFormSubmit:', e);
        }
    }


    async function handlePreviewRelease() {
        releaseStore.setPreviewRequested($releaseConfigState.generateAiAssets ? 'assets' : 'validation');
    }

	async function handleDeliver() {
        try {
            const data = get(releaseRuntimeState).releaseData;
            if (!data) {
                console.warn('No release data available for delivery');
                return;
            }

            // Pass the full request — workflow derives format-specific delivery ID
            const config = get(releaseConfigState);
            await workflow.deliverRelease(data, config);
        } catch (e) {
            // Error already handled by onError callback
            console.error('💥 Error in handleDeliver:', e);
        }
	}

	// Handle asset generation events
	function handleAssetsGenerated(event: CustomEvent) {
		const { imageUrl, audioUrl, videoUrl } = event.detail;
		releaseStore.setAssetResults({ imageUrl, audioUrl, videoUrl });
	}

	function handleAssetGenerationError(_event: CustomEvent) {
		// Asset errors are already handled by releaseStore.setAssetError() in AssetGenerationPanel.
		// Do NOT call releaseStore.setError() here — it resets generationStatus to 'idle',
		// which sends computedStep back to 'input'.
	}
</script>

<!-- Material Design Layout -->
<div class="flex flex-col h-full">
	<!-- Step Indicator with Progress Animation -->
    {#if $computedStep !== 'input'}
		<div class="relative text-center py-4 bg-gray-100 border-b border-gray-200 overflow-hidden">
            {#if $computedStep === 'preview'}
				<!-- Animated background for asset generation -->
				<div 
					class="absolute inset-0 bg-gradient-to-r from-blue-100 to-blue-200 transition-all duration-700 ease-out"
					style="width: {stepProgress}%"
				></div>
			{/if}
            <span class="relative z-10 text-lg font-semibold {$computedStep === 'preview' ? 'text-blue-800' : 'text-gray-700'}">
				{currentStepNumber}/{totalSteps}
			</span>
		</div>
	{/if}

	<!-- Content Area -->
    <div class="flex-1 p-0 {$computedStep !== 'input' ? 'pt-6' : ''}">
		<!-- Configuration Form -->
        {#if $computedStep === 'input'}
			<ReleaseForm onsubmit={handleFormSubmit} />
		{/if}

		<!-- XML Generation -->
        {#if $computedStep === 'generating-xml'}
            {#if $releaseRuntimeState.generatedXML}
                <XMLDisplay onpreview={handlePreviewRelease} />
            {:else}
                <!-- Loading state while generating XML -->
                <div class="flex items-center justify-center min-h-96">
                    <div class="text-center">
                        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <h3 class="text-lg font-semibold text-gray-900 mb-2">{isApple ? 'Generating Apple iTunes Package' : 'Generating DDEX ERN XML'}</h3>
                        <p class="text-gray-600">Please wait while we create your release file...</p>
                    </div>
                </div>
            {/if}
        {/if}

        {#if $computedStep === 'preview'}
            <AssetGenerationPanel 
                ondeliver={handleDeliver}
                onassetsGenerated={handleAssetsGenerated}
                onerror={handleAssetGenerationError}
            />
        {/if}

		<!-- Delivery States -->
        {#if $computedStep === 'delivering' || $computedStep === 'complete'}
			<DeliveryDisplay />
		{/if}

		<!-- Error State -->
        {#if $computedStep === 'error'}
			<ErrorDisplay />
		{/if}
	</div>
</div>
